/**
 * Dev-only console hooks for triggering notification tours.
 *
 * This is the intended way to manually verify a tour end to end — real
 * `LeftNavBar`, real router, real destination page — rather than a Storybook
 * interaction test doing the same thing. `GlobalNotificationPopup.stories.ts`
 * previously had a story (`TourRunsThroughProfileCreation`) that drove a tour
 * through a live, unmocked `<router-view>` for exactly this kind of coverage,
 * and it hung Storybook's browser-mode test runner in CI (root-caused to
 * that shared, unmocked router-view; see the CI-hang note in PR #3347).
 * Automated coverage instead stays split across cheaper, isolated pieces:
 * `useProductTour.test.ts` (the composable's own logic), the Chromatic
 * baseline in `useProductTour.stories.ts` (the overlay, against a stand-in
 * anchor), and the two notification story files (the CTA's visibility).
 * None of those mount the real app shell, so none of them can tell you the
 * tour actually reaches its real anchors — that's what this is for:
 *
 *     __tourPopup('exports_vessel_schedule')   // as a forced popup
 *     __tourBanner('exports_vessel_schedule')  // ...and as a banner
 *     __tourPopup('nope')                      // a mistyped key
 *
 * The key is required. It used to default to the only tour that existed, but
 * with more than one a default silently seeds someone else's tour when you
 * meant your own — and the whole point of this helper is to check the tour you
 * are working on. Keys are whatever `PRODUCT_TOURS` has; an unknown one is
 * seeded anyway, so you can see what a typo in admin looks like.
 *
 * Both display types, because they are different launches: the popup's CTA
 * acknowledges the notification away on its way out, while the banner's leaves
 * it on screen and only closes its "learn more" dialog.
 *
 * Generalized to any registered tour rather than hardcoded to one: the
 * seeded notification's copy is generic placeholder text naming the tour
 * key, not real announcement copy — the tour itself renders identically
 * regardless of what notification carries its key.
 */
import { PRODUCT_TOURS } from '~/constants/productTours'
import type { GlobalNotification } from '~/services/apiClient'
import {
  GlobalNotificationDisplayType,
  GlobalNotificationLevelEnum,
} from '~/services/apiClient'
import { useGlobalNotificationsStore } from '~/stores/globalNotifications'
import { useUserStore } from '~/stores/user'
import type { UserModule } from '~/types'

declare global {
  interface Window {
    __tourPopup?: (tourKey: string) => void
    __tourBanner?: (tourKey: string) => void
  }
}

function seedNotification(
  tourKey: string,
  displayType: GlobalNotificationDisplayType
): void {
  // Unknown keys are seeded rather than rejected: the backend column is free
  // text, so this is the only way to see what a typo in admin actually does
  // (CTA hidden, warning in the console and in Sentry)
  if (!(tourKey in PRODUCT_TOURS)) {
    console.warn(
      `"${tourKey}" is not a registered tour — the notification will render ` +
        `without a CTA. Registered: ${Object.keys(PRODUCT_TOURS).join(', ')}`
    )
  }
  // Only the popup is gated on a known user id (acknowledgements are recorded
  // per user); without one it would render nothing at all and look like this
  // helper was broken. Banners show for anyone.
  if (
    displayType === GlobalNotificationDisplayType.Popup &&
    !useUserStore().userInfo
  ) {
    console.error('Log in first — popups only render once userInfo has loaded.')
    return
  }

  const notification: GlobalNotification = {
    // A fresh id every call. Acknowledgements are keyed by notification id
    // and deliberately survive logout, so a fixed id would show the popup
    // exactly once per browser and never again
    id: Date.now(),
    level: GlobalNotificationLevelEnum.Success,
    // Generic and keyed off whatever tourKey was passed, not a specific
    // tour's real copy — this is placeholder text for local testing, and
    // real announcement copy is authored per-notification by admin anyway
    title: `Dev preview: ${tourKey}`,
    message: 'Seeded locally for tour testing — not real announcement copy.',
    display_type: displayType,
    active_from: new Date(Date.now() - 60_000).toISOString(),
    active_to: undefined,
    tour_key: tourKey,
  }
  useGlobalNotificationsStore().update(notification)
}

export const install: UserModule = ({ isClient }) => {
  // `import.meta.env.DEV` is inlined at build time, so this whole helper is
  // eliminated from production bundles
  if (!import.meta.env.DEV || !isClient) return

  window.__tourPopup = (tourKey: string) =>
    seedNotification(tourKey, GlobalNotificationDisplayType.Popup)
  window.__tourBanner = (tourKey: string) =>
    seedNotification(tourKey, GlobalNotificationDisplayType.Banner)
}
